'use client';

import { AppLink as Link } from '@/lib/navigation';
import { ArrowLeft, Calendar, CalendarCheck, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ProcessInstanceIdCopy } from '@/components/process-instance-id-copy';
import { Button } from '@/components/ui/button';
import { getHistoricTaskStatePresentation } from '@/lib/camunda/history-task-state';
import type { HistoricTaskInstance } from '@/lib/camunda/history-types';
import { formatDuration } from '@/lib/format-duration';
import { cn } from '@/lib/utils';
import { HistoryMetadataItem } from './history-metadata-item';
import type { ProcessHistoryTaskItem } from './process-history-types';

interface ProcessHistoryTaskHeaderProps {
  taskItem: ProcessHistoryTaskItem;
  processInstanceId: string;
  previous: ProcessHistoryTaskItem | null;
  next: ProcessHistoryTaskItem | null;
  onNavigateTask: (taskId: string) => void;
}

function resolveTaskDurationMillis(task: HistoricTaskInstance): number | null {
  if (task.duration != null) {
    return task.duration;
  }

  if (!task.endTime) {
    return null;
  }

  const millis =
    new Date(task.endTime).getTime() - new Date(task.startTime).getTime();

  return Number.isFinite(millis) && millis >= 0 ? millis : null;
}

export function ProcessHistoryTaskHeader({
  taskItem,
  processInstanceId,
  previous,
  next,
  onNavigateTask,
}: ProcessHistoryTaskHeaderProps) {
  const { label, className } = getHistoricTaskStatePresentation(taskItem.task);
  const durationMillis = resolveTaskDurationMillis(taskItem.task);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" className="-ml-2 w-fit px-2" asChild>
          <Link href={`/task-list/participated/${processInstanceId}`}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Volver al histórico
          </Link>
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!previous?.canViewDetail}
            onClick={() => {
              if (previous?.canViewDetail) {
                onNavigateTask(previous.task.id);
              }
            }}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!next?.canViewDetail}
            onClick={() => {
              if (next?.canViewDetail) {
                onNavigateTask(next.task.id);
              }
            }}
          >
            Siguiente
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <h1 className="min-w-0 text-xl font-semibold leading-tight">
            {taskItem.displayName}
          </h1>
          <Badge variant="outline" className={cn('shrink-0', className)}>
            {label}
          </Badge>
        </div>

        <div className="flex flex-col gap-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-6">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <HistoryMetadataItem
              icon={<Calendar className="size-3.5" />}
              label="Inicio"
              value={new Date(taskItem.task.startTime).toLocaleString()}
            />
            {taskItem.task.endTime ? (
              <HistoryMetadataItem
                icon={<CalendarCheck className="size-3.5" />}
                label="Fin"
                value={new Date(taskItem.task.endTime).toLocaleString()}
              />
            ) : null}
            {durationMillis != null ? (
              <HistoryMetadataItem
                icon={<Clock className="size-3.5" />}
                label="Duración"
                value={formatDuration(durationMillis)}
              />
            ) : null}
          </div>

          <ProcessInstanceIdCopy
            processInstanceId={processInstanceId}
            className="sm:shrink-0"
            textClassName="max-w-[min(48vw,34rem)] leading-none text-gray-500"
          />
        </div>
      </div>
    </div>
  );
}
